import { Box, Typography, Button as PriButton } from "@mui/material"
import Autocomplete from "@mui/material/Autocomplete"
import {
  DataGrid,
  GridEventListener,
  GridRowSelectionModel,
} from "@mui/x-data-grid"
import React, { useEffect, useMemo, useRef, useState } from "react"
import {
  purchaseOrderAddLineItems,
  purchaseOrderCopyPasteRowData,
  purchaseOrderUpdateLineItem,
  purchaseOrderUpdateRowSelection,
  selectEnteredInvoiceInformation,
  selectPoLinksTableColumns,
  selectPoLinksTableHiddenColumns,
  updatePoLinksTableColumns,
  updatePoLinksTableHiddenColumns,
  selectOpco,
  updateInvoiceDetail,
  selectLGInternalUser,
} from "../../activeScreenSlice"
import {
  ContentTitle,
} from "../../screens/InvoiceDetail/InvoiceDetail.styles"
import {
  PurchaseOrderSearchRead,
  ReferenceDataService,
  UiEditContainer,
} from "../../api/generated"
import { useAppDispatch, useAppSelector } from "../../app/hooks"
import { useDebounce } from "../../hooks/useDebounce"
import { getPurchaseOrderDetail, searchPurchaseOrders } from "../../service"
import EditPoLineModal, {
  DataToPopulate,
} from "../EditPoLineModal/EditPoLineModal"
import {
  DataGridWrapper,
  DraggedColumn,
  DropLine,
  PickupBox,
  PickupDropContainer,
} from "../SharedGridStyles/SharedGrid.styles"
import { tempColumns as gridColumnsFromSchema } from "./PurchaseOrderLinks.schema"
import {
  ContentWrapper,
  PurchaseOrderHeader,
  PurchaseOrderHeaderElement,
  PurchaseOrderHeaderLabel,
  PurchaseOrderHeaderNumberDisplay,
  PurchaseOrderInstructions,
  PurchaseorderheaderNumberDisplayError,
  SearchBar,
  Spacer,
} from "./PurchaseOrderLinks.styles"
import { useQuery } from "@tanstack/react-query"
import { TAX_CODES_QUERY_KEY } from "../../constants"
import { getSupplierDetails } from "../../service/SupplierDetails"
import useShowToast from "../../hooks/useShowToast"
import { addCustomFunctions } from "./PurchaseOrderLinks.schema.functions"

export default function PurchaseOrderLinks() {
  const LGInternalUser = useAppSelector(selectLGInternalUser);
  const showToast = useShowToast()
  const invoiceInputs = useAppSelector(selectEnteredInvoiceInformation)
  const [value, setValue] = useState<null | string>(null)
  const [inputValue, setInputValue] = useState("")
  const debouncedInputValue = useDebounce(inputValue)
  const [purchaseOrderOptions, setPurchaseOrderOptions] = useState<
    PurchaseOrderSearchRead[]
  >([])
  const [editPoLine, setEditPoLine] = useState<DataToPopulate | undefined>()

  const [copyMode, setCopyMode] = useState<
    "disabled" | "sourceSelecting" | "destinationSelecting"
  >("disabled")
  const [rowIdForCopying, setRowIdForCopying] = useState<string | undefined>(
    undefined,
  )

  const persistedGridColumns = useAppSelector(selectPoLinksTableColumns)
  const persistedGridColumnsWithCustomFunctions =
    addCustomFunctions(persistedGridColumns)
  const persistedGridHiddenColumns = useAppSelector(
    selectPoLinksTableHiddenColumns,
  )

  const persistedIsDifferentFromSchema =
    JSON.stringify(persistedGridColumns) !==
    JSON.stringify(gridColumnsFromSchema)

  const columnsHaveBeenModified =
    persistedIsDifferentFromSchema ||
    (persistedGridHiddenColumns &&
      Object.keys(persistedGridHiddenColumns).length > 0)

  const [pickedColumn, setPickedColumn] = useState(null)
  const [pickedColumnTitle, setPickedColumnTitle] = useState(null)
  const [hoverColumn, setHoverColumn] = useState(null)
  const [dropColumn, setDropColumn] = useState(null)
  const [dragPosition, setDragPosition] = useState({ x: 0, y: 0 })
  const dragElementRef = useRef(null)

  useEffect(() => {
    if (dropColumn) {
      const found = persistedGridColumns.find(
        (col) => col.field === pickedColumn,
      )

      if (found) {
        const updatedColumns = persistedGridColumns.filter(
          (col) => col.field !== pickedColumn,
        )
        const dropIndex = updatedColumns.findIndex(
          (col) => col.field === dropColumn,
        )
        updatedColumns.splice(dropIndex, 0, found)
        setDropColumn(undefined)
        dispatch(updatePoLinksTableColumns(updatedColumns))
      }
    }
  }, [pickedColumn, dropColumn])

  const handleColumnHeaderClick = (event, column: string, title: string) => {
    event.preventDefault()
    event.stopPropagation()

    setPickedColumn(column)
    setPickedColumnTitle(title)
    setDragPosition({ x: event.clientX, y: event.clientY })

    document.addEventListener("mousemove", handleMouseMove)
    document.addEventListener("mouseup", handleMouseUp)
  }

  const handleMouseMove = (event) => {
    const newX = event.clientX
    const newY = event.clientY
    setDragPosition({ x: newX, y: newY })
  }

  const handleMouseUp = () => {
    setPickedColumn(null)
    setPickedColumnTitle(null)
    setHoverColumn(null)

    document.removeEventListener("mousemove", handleMouseMove)
    document.removeEventListener("mouseup", handleMouseUp)
  }

  const opco = useAppSelector(selectOpco)

  const taxCodes = useQuery({
    queryKey: [TAX_CODES_QUERY_KEY, opco],
    queryFn: () => {
      return ReferenceDataService.getAllTaxCodesDataTaxCodesAllGet(opco)
    },
    placeholderData: [],
  }).data.map((item) => item.tax_code)

  const dispatch = useAppDispatch()
  useEffect(() => {
    if (debouncedInputValue && debouncedInputValue.length >= 3) {
      searchPurchaseOrders(debouncedInputValue,opco).then((res) => {
        setPurchaseOrderOptions(res)
      })
    }
  }, [debouncedInputValue])

  function handleUpdateStore(key: string, value?: any) {
    dispatch(updateInvoiceDetail({ key: key, value: value }))
  }

  type SelectedPo = {
    id: number
    label: string
    supplierId: number
    supplierSiteCode: string
  }

  function handleValueChange(
    event: React.SyntheticEvent<Element, Event>,
    selectedPo: SelectedPo,
  ) {
    if (!selectedPo) {
      return
    }
    if (selectedPo.supplierId && selectedPo.supplierSiteCode) {
      populateInvoiceWithSupplierDetails(
        selectedPo.supplierId,
        selectedPo.supplierSiteCode,
      ).then(() => {
        showToast(
          `Updated supplier details from purchase order number ${selectedPo.label}`,
          {
            autoHideDuration: 10000,
          },
        )
      })
    }
    getPurchaseOrderDetail(selectedPo.id).then((res) => {
      dispatch(purchaseOrderAddLineItems(res))
      setInputValue("")
      setValue(null)
    })
  }

  async function populateInvoiceWithSupplierDetails(
    supplierId: number,
    supplierSiteCode: string,
  ) {
    try {
      const res = await getSupplierDetails(supplierId)
      if (!res) return

      const { id, supplier_name, supplier_number, supplier_sites } = res

      const supplierSiteOptions = supplier_sites.map((site) => ({
        id: site.id,
        name: site.site_code,
      }))

      const supplierSiteId =
        supplier_sites.find((site) => site.site_code === supplierSiteCode)
          ?.id || null

      if (id) handleUpdateStore("supplierId", id)
      if (supplier_name) handleUpdateStore("supplierName", supplier_name)
      if (supplier_number) handleUpdateStore("supplierNumber", supplier_number)
      handleUpdateStore("supplierSiteOptions", supplierSiteOptions)
      if (supplierSiteId) handleUpdateStore("supplierSiteId", supplierSiteId)
    } catch (error) {
      console.error("Failed to fetch supplier details:", error)
    }
  }

  function handleSelectionUpdate(newRowSelectionModel: GridRowSelectionModel) {
    dispatch(purchaseOrderUpdateRowSelection(newRowSelectionModel))
  }

  const handleRowClick: GridEventListener<"rowClick"> = (
    params,
    event,
    details,
  ) => {
    const rowId = params.row.temp_line_identifier
    const record = invoiceInputs.lineItems.find(
      (i) => i.temp_line_identifier === rowId,
    )
    if (!record) {
      return
    }

    if (copyMode === "sourceSelecting") {
      setRowIdForCopying(rowId)
      setCopyMode("destinationSelecting")
    } else if (copyMode === "destinationSelecting") {
      const sourceRecord = invoiceInputs.lineItems.find(
        (i) => i.temp_line_identifier === rowIdForCopying,
      )
      if (!sourceRecord) {
        return
      }

      dispatch(
        purchaseOrderCopyPasteRowData({
          source: sourceRecord.temp_line_identifier,
          destination: record.temp_line_identifier,
        }),
      )
      setCopyMode("disabled")
    } else {
      const dataToPopulate: DataToPopulate = {
        tempLineIdentifier: record.temp_line_identifier,
        lineType: record.non_editable_po_fields
          ? record.non_editable_po_fields.line_type
          : undefined,
        lineAmount: record.amount,
        taxAmount: record.tax_amount,
        codeCombination: record.non_editable_po_fields
          ? record.non_editable_po_fields.distribution_code
          : undefined,
        quantity: record.quantity,
        price: record.unit_price,
        unitOfMeasure: record.unit,
        shipmentNumber: record.non_editable_po_fields
          ? record.non_editable_po_fields.shipment_number
          : undefined,
        description: record.description,
        taxCode: record.tax_classification_code,
      }

      setEditPoLine(dataToPopulate)
    }
  }

  const sortedPOLines = useMemo(() => {
    const sorted = [...invoiceInputs.lineItems]
    sorted.sort((a, b) => {
      if (
        a.invoice_line_number === undefined ||
        a.invoice_line_number === null
      ) {
        return 1
      }
      if (
        b.invoice_line_number === undefined ||
        b.invoice_line_number === null
      ) {
        return -1
      }
      return a.invoice_line_number - b.invoice_line_number
    })
    return sorted
  }, [invoiceInputs.lineItems])

  function handleModalSave(data: any) {
    if (data.lineAmount === "-") {
      data.lineAmount = 0
    }
    if (data.quantity === "-") {
      data.quantity = 0
    }
    if (data.price === "-") {
      data.price = 0
    }
    dispatch(purchaseOrderUpdateLineItem(data))
    dispatch(purchaseOrderUpdateRowSelection(invoiceInputs.rowSelectionModel))
    setEditPoLine(undefined)
  }

  return (
    <>
      <ContentWrapper>
        <Box sx={{display: 'flex', flexDirection: 'row', width: '100%'}}>
          <ContentTitle>Line details</ContentTitle>
          <Box sx={{ display: "flex", gap: "16px", }}>
            <PriButton
              variant="outlined"
              disabled={copyMode !== "disabled" || LGInternalUser}
              onClick={() =>
                dispatch(
                  purchaseOrderAddLineItems([
                    {
                      temp_line_identifier: crypto.randomUUID(),
                      non_editable_po_fields: {},
                      invoice_line_number: undefined,
                    } as UiEditContainer,
                  ]),
                )
              }
            >
              Add new line
            </PriButton>
            <PriButton
              variant="outlined"
              disabled={copyMode !== "disabled" || LGInternalUser}
              onClick={() => {
                if (copyMode === "disabled") {
                  setCopyMode("sourceSelecting")
                } else {
                  setRowIdForCopying(undefined)
                  setCopyMode("disabled")
                }
              }}
            >
              {copyMode === "disabled" ? "Copy line content" : "Cancel"}
            </PriButton>
          </Box>
        </Box>
        <PurchaseOrderInstructions>
          Please select appropriate invoice lines from the list below.
        </PurchaseOrderInstructions>
        <PurchaseOrderHeader>
          <PurchaseOrderHeaderElement>
            <PurchaseOrderHeaderLabel>Selected lines</PurchaseOrderHeaderLabel>
            <PurchaseOrderHeaderNumberDisplay>
              {invoiceInputs.purchaseOrderRowSelectionCount}
            </PurchaseOrderHeaderNumberDisplay>
          </PurchaseOrderHeaderElement>
          <PurchaseOrderHeaderElement>
            <PurchaseOrderHeaderLabel>Amount</PurchaseOrderHeaderLabel>
            <PurchaseOrderHeaderNumberDisplay>
              {invoiceInputs.purchaseOrderRowSelectionSum.toLocaleString()}
            </PurchaseOrderHeaderNumberDisplay>
          </PurchaseOrderHeaderElement>
          <PurchaseOrderHeaderElement>
            <PurchaseOrderHeaderLabel>Mismatch</PurchaseOrderHeaderLabel>
            {invoiceInputs.purchaseOrderRowSelectionMismatch === 0 ? (
              <PurchaseOrderHeaderNumberDisplay>
                {invoiceInputs.purchaseOrderRowSelectionMismatch}
              </PurchaseOrderHeaderNumberDisplay>
            ) : (
              <PurchaseorderheaderNumberDisplayError>
                {invoiceInputs.purchaseOrderRowSelectionMismatch.toLocaleString()}
              </PurchaseorderheaderNumberDisplayError>
            )}
          </PurchaseOrderHeaderElement>
          <Spacer />
          {columnsHaveBeenModified && (
            <PriButton
              variant="outlined"
              onClick={() => {
                dispatch(updatePoLinksTableColumns(gridColumnsFromSchema))
                dispatch(updatePoLinksTableHiddenColumns({}))
              }}
              sx={{fontSize: '12px', minWidth: 115}}
            >
              Reset Columns
            </PriButton>
          )}

          <Autocomplete
            value={value}
            onChange={(event, newValue, reason, details) => {
              if (typeof newValue === "string") {
                return
              } else if (newValue) {
                handleValueChange(event, newValue)
              }
            }}
            inputValue={inputValue}
            onInputChange={(event, newInputValue) => {
              setInputValue(newInputValue)
            }}
            freeSolo
            options={purchaseOrderOptions.map(
              (po): SelectedPo => ({
                id: po.id,
                label: po.purchase_order_number,
                supplierId: po.supplier_id,
                supplierSiteCode: po.supplier_site_code,
              }),
            )}
            renderInput={(params) => (
              <SearchBar {...params} label="Search by PO number" />
            )}
          />
        </PurchaseOrderHeader>
        {copyMode === "sourceSelecting" && (
          <Typography>Select source row for copying</Typography>
        )}
        {copyMode === "destinationSelecting" && (
          <Typography>Select destination row for copying</Typography>
        )}
        <DataGridWrapper>
          <DataGrid
            sx={{
              "& .MuiDataGrid-row": {
                cursor: "pointer",
              },
            }}
            // @ts-ignore columns follow different format
            rows={sortedPOLines}
            columns={persistedGridColumnsWithCustomFunctions.map((column) => ({
              ...column,
              renderHeader: (params) => (
                <PickupDropContainer>
                  {hoverColumn === params.field && <DropLine></DropLine>}
                  <PickupBox
                    onMouseDown={(event) =>
                      handleColumnHeaderClick(
                        event,
                        params.field,
                        params.colDef.headerName,
                      )
                    }
                    onMouseUp={() => setDropColumn(params.field)}
                    onMouseOver={() =>
                      pickedColumn && setHoverColumn(params.field)
                    }
                    onMouseOut={() => pickedColumn && setHoverColumn(null)}
                  >
                    {params.colDef.headerName}
                  </PickupBox>
                </PickupDropContainer>
              ),
            }))}
            initialState={{
              pagination: {
                paginationModel: {
                  pageSize: 20,
                },
              },
            }}
            pageSizeOptions={[20]}
            checkboxSelection
            onRowSelectionModelChange={(newRowSelectionModel) => {
              handleSelectionUpdate(newRowSelectionModel)
            }}
            rowSelectionModel={invoiceInputs.rowSelectionModel}
            getRowId={(row) => row.temp_line_identifier}
            disableRowSelectionOnClick
            onRowClick={handleRowClick}
            columnVisibilityModel={persistedGridHiddenColumns}
            onColumnVisibilityModelChange={(newColumnVisibilityModel) =>
              dispatch(
                updatePoLinksTableHiddenColumns(newColumnVisibilityModel),
              )
            }
          />
        </DataGridWrapper>
        
        {editPoLine && (
          <EditPoLineModal
            open={!!editPoLine}
            onClose={() => setEditPoLine(undefined)}
            onSave={(val) => handleModalSave(val)}
            existingData={editPoLine}
            taxCodes={taxCodes}
          />
        )}
      </ContentWrapper>
      {pickedColumn && (
        <DraggedColumn
          ref={dragElementRef}
          style={{
            top: dragPosition.y,
            left: dragPosition.x,
          }}
        >
          {pickedColumnTitle}
        </DraggedColumn>
      )}
    </>
  )
}
